import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { tradingAPI, userAPI } from '../../services/api';
import TradingChart from '../trading/TradingChart';
import { ChevronLeft, ChevronDown, Search, X, Check, Star, Plus, Home, TrendingUp, Wallet, Target, Rocket, User, Clock, RefreshCw } from 'lucide-react';

// Import refactored trading components
import { 
  TRADING_COLORS, 
  CRYPTO_ICONS,
  injectTradingAnimations 
} from '../trading/constants';
import CoinIcon from '../trading/CoinIcon';
import SymbolPicker from '../trading/SymbolPicker';
import OrderBook from '../trading/OrderBook';

// ==================== NATIVE-LIKE ANIMATIONS CSS ====================
const animationStyles = `
  @keyframes slideUp {
    from { transform: translateY(100%); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
  }
  @keyframes slideDown {
    from { transform: translateY(-20px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
  }
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  @keyframes scaleIn {
    from { transform: scale(0.95); opacity: 0; }
    to { transform: scale(1); opacity: 1; }
  }
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.7; }
  }
  @keyframes priceFlash {
    0% { background: transparent; }
    50% { background: rgba(14, 203, 129, 0.2); }
    100% { background: transparent; }
  }
  @keyframes priceFlashDown {
    0% { background: transparent; }
    50% { background: rgba(246, 70, 93, 0.2); }
    100% { background: transparent; }
  }
  .animate-slideUp { animation: slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1); }
  .animate-slideDown { animation: slideDown 0.25s ease-out; }
  .animate-fadeIn { animation: fadeIn 0.2s ease-out; }
  .animate-scaleIn { animation: scaleIn 0.2s cubic-bezier(0.16, 1, 0.3, 1); }
  .animate-pulse { animation: pulse 2s ease-in-out infinite; }
  .btn-press { transition: transform 0.1s ease, opacity 0.1s ease; }
  .btn-press:active { transform: scale(0.96); opacity: 0.9; }
  .tab-transition { transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1); }
  .price-flash-up { animation: priceFlash 0.5s ease-out; }
  .price-flash-down { animation: priceFlashDown 0.5s ease-out; }
`;

// Inject animation styles
if (typeof document !== 'undefined' && !document.getElementById('mobile-trading-animations')) {
  const styleEl = document.createElement('style');
  styleEl.id = 'mobile-trading-animations';
  styleEl.textContent = animationStyles;
  document.head.appendChild(styleEl);
}

// Use imported theme colors (alias for backward compatibility)
const C = TRADING_COLORS;
const ICONS = CRYPTO_ICONS;

// ==================== LOCAL COINICON (removed - using imported) ====================
// CoinIcon is now imported from '../trading/CoinIcon'

// ==================== SYMBOL PICKER (removed - using imported) ====================
// SymbolPicker is now imported from '../trading/SymbolPicker'

// ==================== ORDER BOOK (removed - using imported) ====================
// OrderBook is now imported from '../trading/OrderBook'

// ==================== POSITIONS ====================
const Positions = ({ refresh, onClose }) => {
  const [positions, setPositions] = useState([]);
  const [prices, setPrices] = useState({});
  const [loading, setLoading] = useState(true);
  const [sltpModal, setSltpModal] = useState({ open: false, position: null });
  const [closeModal, setCloseModal] = useState({ open: false, position: null });
  const [closing, setClosing] = useState(false);
  const [slValue, setSlValue] = useState('');
  const [tpValue, setTpValue] = useState('');
  const [savingSltp, setSavingSltp] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await tradingAPI.getTrades('open');
        const openPositions = res.data?.filter(t => t.status === 'open') || [];
        setPositions(openPositions);
        
        // Fetch current prices for all symbols
        const uniqueSymbols = [...new Set(openPositions.map(p => p.symbol))];
        const pricePromises = uniqueSymbols.map(sym => 
          tradingAPI.getPrice(sym).then(r => ({ symbol: sym, price: r.data?.price || 0 }))
        );
        const priceResults = await Promise.all(pricePromises);
        const priceMap = {};
        priceResults.forEach(p => { priceMap[p.symbol] = p.price; });
        setPrices(priceMap);
      } catch (e) {
        console.error('Failed to load positions:', e);
      }
      setLoading(false);
    };
    load();
    const interval = setInterval(load, 3000);
    return () => clearInterval(interval);
  }, [refresh]);

  const openSltpModal = (pos) => {
    setSltpModal({ open: true, position: pos });
    setSlValue(pos.stop_loss?.toString() || '');
    setTpValue(pos.take_profit?.toString() || '');
  };

  const closeSltpModal = () => {
    setSltpModal({ open: false, position: null });
    setSlValue('');
    setTpValue('');
  };

  const handleSaveSLTP = async () => {
    if (!sltpModal.position) return;
    setSavingSltp(true);
    try {
      await tradingAPI.updateSlTp(sltpModal.position.id, {
        stop_loss: slValue ? parseFloat(slValue) : null,
        take_profit: tpValue ? parseFloat(tpValue) : null,
      });
      closeSltpModal();
      onClose?.(); // Refresh positions
    } catch (e) {
      console.error('Failed to update SL/TP:', e);
    }
    setSavingSltp(false);
  };

  const handleClosePosition = async () => {
    if (!closeModal.position) return;
    setClosing(true);
    try {
      await tradingAPI.closeTrade(closeModal.position.id);
      setCloseModal({ open: false, position: null });
      onClose?.(); // Refresh positions
    } catch (e) {
      console.error('Failed to close position:', e);
    }
    setClosing(false);
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: C.textSecondary }}>Загрузка...</div>;
  if (positions.length === 0) return <div style={{ padding: '60px 24px', textAlign: 'center', color: C.textTertiary, fontSize: 14 }}>Нет открытых позиций</div>;

  return (
    <>
      <div style={{ padding: 12 }}>
        {positions.map((pos, i) => {
          const currentPrice = prices[pos.symbol] || pos.current_price || pos.entry_price;
          // Calculate PnL based on current price
          const priceDiff = currentPrice - pos.entry_price;
          const calculatedPnl = pos.side === 'long' 
            ? priceDiff * pos.quantity 
            : -priceDiff * pos.quantity;
          const pnl = pos.pnl || calculatedPnl || 0;
          const pnlPct = pos.margin > 0 ? (pnl / pos.margin * 100) : 0;
          const isProfit = pnl >= 0;
          const liqPrice = pos.liquidation_price || (pos.side === 'long' ? pos.entry_price * (1 - 0.9 / pos.leverage) : pos.entry_price * (1 + 0.9 / pos.leverage));
          const hasSLTP = pos.stop_loss || pos.take_profit;

          return (
            <div key={i} style={{ background: C.bgSecondary, borderRadius: 12, padding: 14, marginBottom: 10, border: `1px solid ${C.border}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <CoinIcon symbol={pos.symbol} size={36} />
                  <div>
                    <div style={{ color: C.text, fontWeight: 600, fontSize: 16 }}>{pos.symbol?.replace('USDT', '')}/USDT</div>
                    <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                      <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: pos.side === 'long' ? C.longBg : C.shortBg, color: pos.side === 'long' ? C.long : C.short }}>{pos.side?.toUpperCase()}</span>
                      <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: C.yellowSoft, color: C.yellow }}>{pos.leverage}x</span>
                    </div>
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ color: isProfit ? C.long : C.short, fontSize: 18, fontWeight: 700, fontFamily: 'monospace' }}>{isProfit ? '+' : ''}{pnl.toFixed(2)}</div>
                  <div style={{ color: isProfit ? C.long : C.short, fontSize: 12, marginTop: 2 }}>{isProfit ? '+' : ''}{pnlPct.toFixed(2)}%</div>
                </div>
              </div>
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px 16px', padding: 12, background: C.bgTertiary, borderRadius: 8, marginBottom: 12 }}>
                <div><div style={{ color: C.textTertiary, fontSize: 11 }}>Размер</div><div style={{ color: C.text, fontSize: 13, marginTop: 2 }}>{pos.quantity?.toFixed(4)}</div></div>
                <div><div style={{ color: C.textTertiary, fontSize: 11 }}>Маржа</div><div style={{ color: C.text, fontSize: 13, marginTop: 2 }}>${pos.margin?.toFixed(2)}</div></div>
                <div><div style={{ color: C.textTertiary, fontSize: 11 }}>Цена входа</div><div style={{ color: C.text, fontSize: 13, marginTop: 2 }}>${pos.entry_price?.toFixed(2)}</div></div>
                <div>
                  <div style={{ color: C.textTertiary, fontSize: 11 }}>Текущая / Ликв.</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                    <span style={{ color: isProfit ? C.long : C.short, fontSize: 13 }}>${currentPrice?.toFixed(2)}</span>
                    <span style={{ color: C.textMuted, fontSize: 10 }}>/</span>
                    <span style={{ color: C.short, fontSize: 11 }}>${liqPrice?.toFixed(0)}</span>
                  </div>
                </div>
              </div>

              {/* SL/TP Info Row */}
              {hasSLTP && (
                <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                  {pos.stop_loss && (
                    <div style={{ flex: 1, padding: '8px 10px', borderRadius: 6, background: C.shortBg, border: `1px solid ${C.short}30` }}>
                      <div style={{ color: C.textTertiary, fontSize: 10 }}>Stop Loss</div>
                      <div style={{ color: C.short, fontSize: 13, fontWeight: 600, fontFamily: 'monospace' }}>${pos.stop_loss.toFixed(2)}</div>
                    </div>
                  )}
                  {pos.take_profit && (
                    <div style={{ flex: 1, padding: '8px 10px', borderRadius: 6, background: C.longBg, border: `1px solid ${C.long}30` }}>
                      <div style={{ color: C.textTertiary, fontSize: 10 }}>Take Profit</div>
                      <div style={{ color: C.long, fontSize: 13, fontWeight: 600, fontFamily: 'monospace' }}>${pos.take_profit.toFixed(2)}</div>
                    </div>
                  )}
                </div>
              )}
              
              <div style={{ display: 'flex', gap: 8 }}>
                <button 
                  onClick={() => openSltpModal(pos)} 
                  style={{ 
                    flex: 1, padding: 12, borderRadius: 8, 
                    background: hasSLTP ? C.yellowSoft : C.bgElevated, 
                    color: C.yellow, 
                    border: `1px solid ${C.yellow}40`, 
                    fontSize: 13, fontWeight: 600, 
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 
                  }}
                >
                  <Target size={16} />{hasSLTP ? 'Изменить TP/SL' : 'Добавить TP/SL'}
                </button>
                <button 
                  onClick={() => setCloseModal({ open: true, position: { ...pos, calculatedPnl: pnl, currentPrice } })} 
                  style={{ flex: 1, padding: 12, borderRadius: 8, background: C.shortBg, color: C.short, border: 'none', fontSize: 13, fontWeight: 600 }}
                >
                  Закрыть
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* SL/TP Modal */}
      {sltpModal.open && sltpModal.position && (
        <div className="animate-fadeIn" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000, display: 'flex', alignItems: 'flex-end' }} onClick={closeSltpModal}>
          <div className="animate-slideUp" style={{ width: '100%', background: C.bgSecondary, borderRadius: '16px 16px 0 0', paddingBottom: 'env(safe-area-inset-bottom)' }} onClick={e => e.stopPropagation()}>
            <div style={{ width: 40, height: 4, background: C.textMuted, borderRadius: 2, margin: '12px auto' }} />
            <div style={{ padding: '8px 16px 24px' }}>
              <h3 style={{ color: C.text, fontSize: 18, fontWeight: 600, marginBottom: 8, textAlign: 'center' }}>TP/SL для {sltpModal.position.symbol}</h3>
              
              <div style={{ padding: '10px 14px', background: C.bgTertiary, borderRadius: 8, marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ color: C.textTertiary, fontSize: 11 }}>Цена входа</div>
                  <div style={{ color: C.text, fontSize: 14, fontWeight: 600 }}>${sltpModal.position.entry_price?.toFixed(2)}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ color: C.textTertiary, fontSize: 11 }}>Направление</div>
                  <div style={{ color: sltpModal.position.side === 'long' ? C.long : C.short, fontSize: 14, fontWeight: 600 }}>{sltpModal.position.side?.toUpperCase()}</div>
                </div>
              </div>

              {/* Take Profit Input */}
              <div style={{ marginBottom: 12 }}>
                <label style={{ color: C.textTertiary, fontSize: 12, marginBottom: 6, display: 'block' }}>Take Profit (цена)</label>
                <div style={{ display: 'flex', alignItems: 'center', background: C.bgInput, borderRadius: 8, padding: '4px 14px', border: `1px solid ${C.long}40` }}>
                  <input 
                    type="number" 
                    value={tpValue} 
                    onChange={(e) => setTpValue(e.target.value)} 
                    placeholder="Не установлен"
                    style={{ flex: 1, background: 'none', border: 'none', outline: 'none', color: C.long, fontSize: 16, fontFamily: 'monospace', padding: '10px 0' }} 
                  />
                  <span style={{ color: C.textTertiary, fontSize: 12 }}>USDT</span>
                </div>
                {tpValue && (
                  <div style={{ color: C.long, fontSize: 11, marginTop: 4 }}>
                    Прибыль: +${((parseFloat(tpValue) - sltpModal.position.entry_price) * sltpModal.position.quantity * (sltpModal.position.side === 'long' ? 1 : -1)).toFixed(2)}
                  </div>
                )}
              </div>

              {/* Stop Loss Input */}
              <div style={{ marginBottom: 20 }}>
                <label style={{ color: C.textTertiary, fontSize: 12, marginBottom: 6, display: 'block' }}>Stop Loss (цена)</label>
                <div style={{ display: 'flex', alignItems: 'center', background: C.bgInput, borderRadius: 8, padding: '4px 14px', border: `1px solid ${C.short}40` }}>
                  <input 
                    type="number" 
                    value={slValue} 
                    onChange={(e) => setSlValue(e.target.value)} 
                    placeholder="Не установлен"
                    style={{ flex: 1, background: 'none', border: 'none', outline: 'none', color: C.short, fontSize: 16, fontFamily: 'monospace', padding: '10px 0' }} 
                  />
                  <span style={{ color: C.textTertiary, fontSize: 12 }}>USDT</span>
                </div>
                {slValue && (
                  <div style={{ color: C.short, fontSize: 11, marginTop: 4 }}>
                    Убыток: ${((parseFloat(slValue) - sltpModal.position.entry_price) * sltpModal.position.quantity * (sltpModal.position.side === 'long' ? 1 : -1)).toFixed(2)}
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={closeSltpModal} style={{
                  flex: 1, padding: 14, borderRadius: 8, background: C.bgTertiary, border: 'none', color: C.text, fontSize: 15, fontWeight: 600,
                }}>Отмена</button>
                <button onClick={handleSaveSLTP} disabled={savingSltp} style={{
                  flex: 1, padding: 14, borderRadius: 8, background: C.yellow, border: 'none', color: '#000', fontSize: 15, fontWeight: 700, opacity: savingSltp ? 0.6 : 1,
                }}>{savingSltp ? 'Сохранение...' : 'Сохранить'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Close Position Confirmation Modal */}
      {closeModal.open && closeModal.position && (
        <div className="animate-fadeIn" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} onClick={() => setCloseModal({ open: false, position: null })}>
          <div className="animate-scaleIn" style={{ width: '100%', maxWidth: 340, background: C.bgSecondary, borderRadius: 16, overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: 20 }}>
              {/* Warning Icon */}
              <div style={{ textAlign: 'center', marginBottom: 16 }}>
                <div style={{ 
                  width: 56, height: 56, 
                  borderRadius: '50%', 
                  background: C.shortBg, 
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  margin: '0 auto'
                }}>
                  <X size={28} color={C.short} />
                </div>
              </div>
              
              <h3 style={{ color: C.text, fontSize: 18, fontWeight: 600, marginBottom: 8, textAlign: 'center' }}>
                Закрыть позицию?
              </h3>
              <p style={{ color: C.textSecondary, fontSize: 14, textAlign: 'center', marginBottom: 16 }}>
                Вы уверены, что хотите закрыть эту позицию?
              </p>

              {/* Position Info */}
              <div style={{ padding: 12, background: C.bgTertiary, borderRadius: 10, marginBottom: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span style={{ color: C.textTertiary, fontSize: 12 }}>Пара</span>
                  <span style={{ color: C.text, fontSize: 13, fontWeight: 600 }}>{closeModal.position.symbol}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span style={{ color: C.textTertiary, fontSize: 12 }}>Направление</span>
                  <span style={{ color: closeModal.position.side === 'long' ? C.long : C.short, fontSize: 13, fontWeight: 600 }}>
                    {closeModal.position.side?.toUpperCase()} {closeModal.position.leverage}x
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span style={{ color: C.textTertiary, fontSize: 12 }}>Текущая цена</span>
                  <span style={{ color: C.text, fontSize: 13, fontWeight: 600 }}>
                    ${closeModal.position.currentPrice?.toFixed(2)}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: C.textTertiary, fontSize: 12 }}>PnL</span>
                  <span style={{ 
                    color: closeModal.position.calculatedPnl >= 0 ? C.long : C.short, 
                    fontSize: 14, 
                    fontWeight: 700 
                  }}>
                    {closeModal.position.calculatedPnl >= 0 ? '+' : ''}{closeModal.position.calculatedPnl?.toFixed(2)} USDT
                  </span>
                </div>
              </div>

              {/* Buttons */}
              <div style={{ display: 'flex', gap: 10 }}>
                <button 
                  onClick={() => setCloseModal({ open: false, position: null })} 
                  style={{
                    flex: 1, padding: 14, borderRadius: 10, 
                    background: C.bgTertiary, border: 'none', 
                    color: C.text, fontSize: 15, fontWeight: 600,
                  }}
                >
                  Отмена
                </button>
                <button 
                  onClick={handleClosePosition} 
                  disabled={closing}
                  style={{
                    flex: 1, padding: 14, borderRadius: 10, 
                    background: C.short, border: 'none', 
                    color: '#fff', fontSize: 15, fontWeight: 700,
                    opacity: closing ? 0.6 : 1,
                  }}
                >
                  {closing ? 'Закрытие...' : 'Да, закрыть'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ==================== PENDING ORDERS (FUTURES & SPOT) ====================
const PendingOrders = ({ refresh, mode }) => {
  const [futuresOrders, setFuturesOrders] = useState([]);
  const [spotOrders, setSpotOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [futuresRes, spotRes] = await Promise.all([
          tradingAPI.getPendingOrders().catch(() => ({ data: [] })),
          tradingAPI.getSpotPendingOrders().catch(() => ({ data: [] }))
        ]);
        setFuturesOrders(futuresRes.data || []);
        setSpotOrders(spotRes.data || []);
      } catch (e) {
        console.error('Failed to load pending orders:', e);
      }
      setLoading(false);
    };
    load();
    const interval = setInterval(load, 5000);
    return () => clearInterval(interval);
  }, [refresh]);

  const handleCancelFutures = async (orderId) => {
    setCancelling(orderId);
    try {
      await tradingAPI.cancelOrder(orderId);
      setFuturesOrders(futuresOrders.filter(o => o.id !== orderId));
    } catch (e) {
      console.error('Failed to cancel futures order:', e);
    }
    setCancelling(null);
  };

  const handleCancelSpot = async (orderId) => {
    setCancelling(orderId);
    try {
      await tradingAPI.cancelSpotOrder(orderId);
      setSpotOrders(spotOrders.filter(o => o.id !== orderId));
    } catch (e) {
      console.error('Failed to cancel spot order:', e);
    }
    setCancelling(null);
  };

  // Filter orders based on current mode
  const orders = mode === 'spot' ? spotOrders : futuresOrders;
  const handleCancel = mode === 'spot' ? handleCancelSpot : handleCancelFutures;

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 40 }}>
        <RefreshCw size={24} color={C.yellow} className="animate-spin" />
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div style={{ padding: '40px 24px', textAlign: 'center' }}>
        <Clock size={40} color={C.textTertiary} style={{ marginBottom: 12 }} />
        <div style={{ color: C.textTertiary, fontSize: 14 }}>Нет отложенных ордеров</div>
        <div style={{ color: C.textMuted, fontSize: 12, marginTop: 6 }}>
          Создайте лимитный ордер во вкладке "Торговля"
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: 12 }}>
      {orders.map((order) => {
        const base = order.symbol?.replace('USDT', '') || '';
        const isLong = order.side === 'long' || order.side === 'buy';
        const priceDiff = order.limit_price && order.current_price 
          ? ((order.limit_price - order.current_price) / order.current_price * 100)
          : 0;

        return (
          <div 
            key={order.id} 
            style={{ 
              background: C.bgSecondary, 
              borderRadius: 12, 
              padding: 14, 
              marginBottom: 10, 
              border: `1px solid ${C.border}`,
            }}
          >
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <CoinIcon symbol={order.symbol} size={36} />
                <div>
                  <div style={{ color: C.text, fontWeight: 600, fontSize: 15 }}>{order.symbol}</div>
                  <div style={{ 
                    display: 'inline-block',
                    padding: '2px 6px', 
                    borderRadius: 4, 
                    background: isLong ? C.longBg : C.shortBg,
                    color: isLong ? C.long : C.short,
                    fontSize: 10,
                    fontWeight: 600,
                    marginTop: 2,
                  }}>
                    {mode === 'spot' 
                      ? (isLong ? 'ПОКУПКА' : 'ПРОДАЖА')
                      : (isLong ? 'LONG' : 'SHORT')
                    } • LIMIT
                  </div>
                </div>
              </div>
              <div style={{ 
                padding: '4px 8px', 
                borderRadius: 6, 
                background: C.yellowSoft,
                display: 'flex',
                alignItems: 'center',
                gap: 4,
              }}>
                <Clock size={12} color={C.yellow} />
                <span style={{ color: C.yellow, fontSize: 11, fontWeight: 600 }}>Ожидает</span>
              </div>
            </div>

            {/* Order Details */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: '1fr 1fr', 
              gap: 8, 
              padding: 10, 
              background: C.bgTertiary, 
              borderRadius: 8,
              marginBottom: 10,
            }}>
              <div>
                <div style={{ color: C.textTertiary, fontSize: 10 }}>Цена исполнения</div>
                <div style={{ color: C.yellow, fontSize: 14, fontWeight: 600, fontFamily: 'monospace' }}>
                  ${order.limit_price?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 7 })}
                </div>
              </div>
              <div>
                <div style={{ color: C.textTertiary, fontSize: 10 }}>Размер</div>
                <div style={{ color: C.text, fontSize: 14, fontWeight: 600, fontFamily: 'monospace' }}>
                  {order.quantity?.toFixed(4)} {base}
                </div>
              </div>
              <div>
                <div style={{ color: C.textTertiary, fontSize: 10 }}>Сумма</div>
                <div style={{ color: C.text, fontSize: 13, fontFamily: 'monospace' }}>
                  ${(order.amount || order.margin || (order.quantity * order.limit_price))?.toFixed(2)}
                </div>
              </div>
              <div>
                <div style={{ color: C.textTertiary, fontSize: 10 }}>От текущей цены</div>
                <div style={{ 
                  color: priceDiff < 0 ? C.long : C.short, 
                  fontSize: 13, 
                  fontWeight: 500,
                  fontFamily: 'monospace' 
                }}>
                  {priceDiff > 0 ? '+' : ''}{priceDiff.toFixed(2)}%
                </div>
              </div>
            </div>

            {/* SL/TP Info */}
            {(order.stop_loss || order.take_profit) && (
              <div style={{ 
                display: 'flex', 
                gap: 8, 
                padding: '8px 10px', 
                background: C.bgElevated, 
                borderRadius: 6,
                marginBottom: 10,
              }}>
                {order.stop_loss && (
                  <div style={{ flex: 1 }}>
                    <div style={{ color: C.textTertiary, fontSize: 9 }}>Stop Loss</div>
                    <div style={{ color: C.short, fontSize: 12, fontWeight: 600 }}>${order.stop_loss.toFixed(2)}</div>
                  </div>
                )}
                {order.take_profit && (
                  <div style={{ flex: 1 }}>
                    <div style={{ color: C.textTertiary, fontSize: 9 }}>Take Profit</div>
                    <div style={{ color: C.long, fontSize: 12, fontWeight: 600 }}>${order.take_profit.toFixed(2)}</div>
                  </div>
                )}
              </div>
            )}

            {/* Cancel Button */}
            <button 
              onClick={() => handleCancel(order.id)}
              disabled={cancelling === order.id}
              style={{ 
                width: '100%', 
                padding: 12, 
                borderRadius: 8, 
                background: C.shortBg, 
                color: C.short, 
                border: `1px solid rgba(239, 68, 68, 0.2)`,
                fontSize: 13, 
                fontWeight: 600,
                opacity: cancelling === order.id ? 0.5 : 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
              }}
            >
              <X size={16} />
              {cancelling === order.id ? 'Отмена...' : 'Отменить ордер'}
            </button>
          </div>
        );
      })}
    </div>
  );
};

// ==================== SPOT HOLDINGS ====================
const SpotHoldings = ({ holdings, onSell }) => {
  if (!holdings || holdings.length === 0) {
    return <div style={{ padding: '40px 24px', textAlign: 'center', color: C.textTertiary, fontSize: 14 }}>Нет спотовых активов</div>;
  }

  return (
    <div style={{ padding: 12 }}>
      {holdings.map((h, i) => {
        const qty = h.quantity || h.amount || 0;
        const currentValue = h.current_value || (qty * (h.current_price || h.avg_buy_price || 0));
        const currentPrice = qty > 0 ? currentValue / qty : 0;
        const pnl = h.pnl || 0;
        const pnlPct = h.pnl_percent || 0;
        const isProfit = pnl >= 0;

        return (
          <div key={i} style={{ background: C.bgSecondary, borderRadius: 12, padding: 14, marginBottom: 10, border: `1px solid ${C.border}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <CoinIcon symbol={h.symbol + 'USDT'} size={36} />
                <div>
                  <div style={{ color: C.text, fontWeight: 600, fontSize: 16 }}>{h.symbol}</div>
                  <div style={{ color: C.textTertiary, fontSize: 12, marginTop: 2 }}>
                    @ ${currentPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ color: C.text, fontSize: 16, fontWeight: 600 }}>{qty.toFixed(6)}</div>
                <div style={{ color: C.yellow, fontSize: 13, marginTop: 2, fontFamily: 'monospace' }}>
                  ≈ ${currentValue.toFixed(2)}
                </div>
              </div>
            </div>
            
            {/* PnL Row */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              padding: '8px 10px', 
              background: C.bgTertiary, 
              borderRadius: 6, 
              marginTop: 10,
              marginBottom: 10
            }}>
              <div>
                <div style={{ color: C.textTertiary, fontSize: 10 }}>Инвестировано</div>
                <div style={{ color: C.text, fontSize: 12 }}>${h.total_invested?.toFixed(2) || '0.00'}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ color: C.textTertiary, fontSize: 10 }}>PnL</div>
                <div style={{ color: isProfit ? C.long : C.short, fontSize: 12, fontWeight: 600 }}>
                  {isProfit ? '+' : ''}{pnl.toFixed(2)} ({pnlPct.toFixed(2)}%)
                </div>
              </div>
            </div>

            <button onClick={() => onSell(h)} style={{ 
              width: '100%', 
              padding: 12, 
              borderRadius: 8, 
              background: C.shortBg, 
              color: C.short, 
              border: 'none', 
              fontSize: 13, 
              fontWeight: 600 
            }}>Продать</button>
          </div>
        );
      })}
    </div>
  );
};

// ==================== TRADE HISTORY ====================
const TradeHistory = ({ mode }) => {
  const [loading, setLoading] = useState(true);
  const [spotHistory, setSpotHistory] = useState([]);
  const [futuresHistory, setFuturesHistory] = useState([]);

  useEffect(() => {
    const loadHistory = async () => {
      setLoading(true);
      try {
        if (mode === 'spot') {
          const res = await tradingAPI.getSpotHistory(50, 0);
          setSpotHistory(res.data || []);
        } else {
          const res = await tradingAPI.getTradeHistory('futures', 50, 0);
          setFuturesHistory(res.data?.trades || []);
        }
      } catch (e) {
        console.error('Failed to load history:', e);
      }
      setLoading(false);
    };
    loadHistory();
  }, [mode]);

  if (loading) {
    return <div style={{ padding: 40, textAlign: 'center', color: C.textSecondary }}>Загрузка...</div>;
  }

  // Spot History
  if (mode === 'spot') {
    if (spotHistory.length === 0) {
      return <div style={{ padding: '60px 24px', textAlign: 'center', color: C.textTertiary, fontSize: 14 }}>Нет истории спот-операций</div>;
    }
    
    return (
      <div style={{ padding: 12 }}>
        {spotHistory.map((t, i) => {
          const isBuy = t.type === 'buy';
          const date = new Date(t.created_at);
          
          return (
            <div key={i} style={{ 
              background: C.bgSecondary, 
              borderRadius: 10, 
              padding: 14, 
              marginBottom: 8, 
              border: `1px solid ${C.border}` 
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <CoinIcon symbol={t.symbol + 'USDT'} size={32} />
                  <div>
                    <div style={{ color: C.text, fontWeight: 600, fontSize: 14 }}>{t.symbol}</div>
                    <div style={{ 
                      color: isBuy ? C.long : C.short, 
                      fontSize: 11, 
                      fontWeight: 600,
                      marginTop: 2 
                    }}>
                      {isBuy ? 'Покупка' : 'Продажа'}
                    </div>
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ color: C.text, fontSize: 14, fontWeight: 600 }}>
                    {t.quantity.toFixed(6)}
                  </div>
                  <div style={{ color: C.yellow, fontSize: 12, marginTop: 2 }}>
                    ${t.total.toFixed(2)}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 8, borderTop: `1px solid ${C.border}` }}>
                <span style={{ color: C.textTertiary, fontSize: 11 }}>
                  Цена: ${t.price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
                <span style={{ color: C.textTertiary, fontSize: 11 }}>
                  {date.toLocaleDateString('ru-RU')} {date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // Futures History
  if (futuresHistory.length === 0) {
    return <div style={{ padding: '60px 24px', textAlign: 'center', color: C.textTertiary, fontSize: 14 }}>Нет истории позиций</div>;
  }

  return (
    <div style={{ padding: 12 }}>
      {futuresHistory.map((t, i) => {
        const isLong = t.side === 'long';
        const pnl = t.pnl || 0;
        const isProfit = pnl >= 0;
        const closedDate = t.closed_at ? new Date(t.closed_at) : null;
        const base = t.symbol?.replace('USDT', '') || 'BTC';
        
        return (
          <div key={i} style={{ 
            background: C.bgSecondary, 
            borderRadius: 10, 
            padding: 14, 
            marginBottom: 8, 
            border: `1px solid ${C.border}` 
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <CoinIcon symbol={t.symbol} size={32} />
                <div>
                  <div style={{ color: C.text, fontWeight: 600, fontSize: 14 }}>{base}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                    <span style={{ 
                      color: isLong ? C.long : C.short, 
                      fontSize: 11, 
                      fontWeight: 600 
                    }}>
                      {isLong ? 'Long' : 'Short'}
                    </span>
                    <span style={{ 
                      fontSize: 10, 
                      color: C.yellow, 
                      background: C.yellowSoft, 
                      padding: '2px 4px', 
                      borderRadius: 3 
                    }}>
                      {t.leverage}x
                    </span>
                    {/* Status badge with icon */}
                    <span style={{ 
                      fontSize: 10, 
                      color: t.status === 'closed' ? C.long : C.short, 
                      background: t.status === 'closed' ? C.longBg : C.shortBg, 
                      padding: '2px 6px', 
                      borderRadius: 3,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 3,
                    }}>
                      {t.status === 'closed' && <Check size={10} />}
                      {t.status === 'closed' ? 'Закрыта' : 'Отменена'}
                    </span>
                  </div>
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ 
                  color: isProfit ? C.long : C.short, 
                  fontSize: 14, 
                  fontWeight: 700 
                }}>
                  {isProfit ? '+' : ''}{pnl.toFixed(2)} USDT
                </div>
              </div>
            </div>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: '1fr 1fr', 
              gap: 8, 
              paddingTop: 8, 
              borderTop: `1px solid ${C.border}` 
            }}>
              <div>
                <div style={{ color: C.textTertiary, fontSize: 10 }}>Вход</div>
                <div style={{ color: C.text, fontSize: 12 }}>${t.entry_price?.toLocaleString()}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ color: C.textTertiary, fontSize: 10 }}>Выход</div>
                <div style={{ color: C.text, fontSize: 12 }}>${(t.exit_price || t.close_price || t.current_price)?.toLocaleString() || '-'}</div>
              </div>
            </div>
            {closedDate && (
              <div style={{ color: C.textTertiary, fontSize: 10, marginTop: 6, textAlign: 'right' }}>
                {closedDate.toLocaleDateString('ru-RU')} {closedDate.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ==================== MAIN COMPONENT ====================
const PremiumMobileTradingPage = ({ selectedSymbol: propSymbol, setSelectedSymbol: propSetSymbol, onTradeOpened }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [symbol, setSymbol] = useState(propSymbol || searchParams.get('symbol') || 'BTCUSDT');
  const [price, setPrice] = useState(0);
  const [ticker, setTicker] = useState(null);
  const [balance, setBalance] = useState(0);
  const [spotHoldings, setSpotHoldings] = useState([]);
  const [showSymbols, setShowSymbols] = useState(false);
  const [refresh, setRefresh] = useState(0);

  // Restore state from URL params
  const [mode, setMode] = useState(searchParams.get('mode') || 'futures');
  const [tab, setTab] = useState(searchParams.get('tab') || 'chart');

  const [leverage, setLeverage] = useState(10);
  const [orderType, setOrderType] = useState('market');
  const [amount, setAmount] = useState('');
  const [limitPrice, setLimitPrice] = useState('');
  const [sliderPct, setSliderPct] = useState(0);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState({ type: '', text: '' });
  const [showLeverageModal, setShowLeverageModal] = useState(false);
  const [showOrderTypeModal, setShowOrderTypeModal] = useState(false);
  const [tradeSide, setTradeSide] = useState('long'); // 'long' = Buy, 'short' = Sell

  // Save tab and mode to URL when they change
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    params.set('tab', tab);
    params.set('mode', mode);
    setSearchParams(params, { replace: true });
  }, [tab, mode, setSearchParams, searchParams]);

  // Reset amount when switching between Spot/Futures
  useEffect(() => {
    setAmount('');
    setSliderPct(0);
    setTradeSide('long');
    setLimitPrice('');
    setOrderType('market');
  }, [mode]);

  // Reset amount when switching between Buy/Sell
  useEffect(() => {
    setAmount('');
    setSliderPct(0);
  }, [tradeSide]);

  // Reset amount and limit price when switching symbol
  useEffect(() => {
    setAmount('');
    setSliderPct(0);
    setLimitPrice('');
  }, [symbol]);

  // Reset amount and limit price when switching tabs (leaving trade tab)
  useEffect(() => {
    if (tab !== 'trade') {
      setAmount('');
      setSliderPct(0);
      setLimitPrice('');
      setOrderType('market');
    }
  }, [tab]);

  useEffect(() => {
    const load = async () => {
      try {
        const [priceRes, tickerRes, balanceRes, holdingsRes] = await Promise.all([
          tradingAPI.getPrice(symbol),
          tradingAPI.getTicker(symbol),
          userAPI.getBalance(),
          tradingAPI.getHoldings().catch(() => ({ data: [] })),
        ]);
        setPrice(priceRes.data?.price || 0);
        setTicker(tickerRes.data);
        setBalance(balanceRes.data?.exchange_balance || 0);
        setSpotHoldings(holdingsRes.data || []);
      } catch (e) { console.error(e); }
    };
    load();
    const interval = setInterval(load, 5000);
    return () => clearInterval(interval);
  }, [symbol]);

  const handleSymbolChange = (s) => { setSymbol(s); propSetSymbol?.(s); };
  const handleSlider = (pct) => { 
    setSliderPct(pct); 
    // При продаже спота - считаем от баланса актива, иначе от USDT
    if (mode === 'spot' && tradeSide === 'short' && spotAssetBalance > 0) {
      const assetValueInUsdt = spotAssetBalance * price;
      setAmount((assetValueInUsdt * pct / 100).toFixed(2));
    } else {
      setAmount((balance * pct / 100).toFixed(2)); 
    }
  };

  const base = symbol.replace('USDT', '');

  const handleTrade = async (side) => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { setMsg({ type: 'error', text: 'Введите сумму' }); return; }
    
    // For sell in spot mode, check asset balance instead of USDT
    if (mode === 'spot' && side === 'short') {
      const assetValue = spotAssetBalance * price;
      if (amt > assetValue) { setMsg({ type: 'error', text: `Недостаточно ${base}` }); return; }
    } else {
      if (amt > balance) { setMsg({ type: 'error', text: 'Недостаточно USDT' }); return; }
    }

    setLoading(true);
    try {
      if (mode === 'spot') {
        if (side === 'long') {
          // Buy: Send symbol with USDT suffix and amount_usdt as per API spec
          await tradingAPI.spotBuy({ 
            symbol: symbol, 
            amount_usdt: amt,
            order_type: orderType,
            limit_price: orderType === 'limit' ? parseFloat(limitPrice) : null
          });
        } else {
          // Sell: Send base symbol (BTC, not BTCUSDT) and quantity
          const sellQuantity = amt / price;
          await tradingAPI.spotSell({ 
            symbol: base, 
            quantity: sellQuantity,
            order_type: orderType,
            limit_price: orderType === 'limit' ? parseFloat(limitPrice) : null
          });
        }
        setMsg({ type: 'success', text: orderType === 'limit' ? 'Лимитный ордер создан' : 'Ордер исполнен' });
      } else {
        await tradingAPI.openTrade({
          symbol, side, type: 'futures', order_type: orderType,
          margin: amt, leverage, 
          limit_price: orderType === 'limit' ? parseFloat(limitPrice) : null,
        });
        setMsg({ type: 'success', text: orderType === 'limit' ? 'Лимитный ордер создан' : 'Ордер исполнен' });
      }
      setAmount(''); setSliderPct(0); setLimitPrice('');
      setRefresh(r => r + 1);
      onTradeOpened?.();
    } catch (e) {
      const errorDetail = e.response?.data?.detail;
      const errorMsg = Array.isArray(errorDetail) ? errorDetail[0]?.msg : (errorDetail || 'Ошибка выполнения');
      setMsg({ type: 'error', text: errorMsg });
    }
    setLoading(false);
    setTimeout(() => setMsg({ type: '', text: '' }), 3000);
  };
  const change = ticker?.priceChangePercent || ticker?.change_percent || 0;
  const isUp = change >= 0;
  const high24 = ticker?.highPrice || ticker?.high_24h || 0;
  const low24 = ticker?.lowPrice || ticker?.low_24h || 0;
  const vol24 = ticker?.volume || ticker?.volume_24h || 0;
  const formatVol = (v) => v >= 1e9 ? `${(v / 1e9).toFixed(2)}B` : v >= 1e6 ? `${(v / 1e6).toFixed(2)}M` : v >= 1e3 ? `${(v / 1e3).toFixed(1)}K` : v?.toFixed(0) || '0';

  const spotAssetBalance = spotHoldings.find(h => h.symbol === base)?.quantity || spotHoldings.find(h => h.symbol === base)?.amount || 0;

  // Calculate bottom padding based on active tab
  const bottomPadding = tab === 'chart' ? 72 : 72; // Chart extends to bottom, buttons overlay

  return (
    <div style={{ minHeight: '100vh', background: C.bg, display: 'flex', flexDirection: 'column', paddingBottom: bottomPadding }}>
      {/* Header */}
      <div style={{ position: 'sticky', top: 0, zIndex: 100, background: C.bgSecondary }}>
        <div style={{ display: 'flex', alignItems: 'center', paddingTop: 'max(8px, env(safe-area-inset-top))', paddingLeft: 12, paddingRight: 12 }}>
          <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', padding: 8, marginRight: 4 }}>
            <ChevronLeft size={22} color={C.text} />
          </button>
          {['Спот', 'Фьючерсы'].map((label) => {
            const key = label === 'Спот' ? 'spot' : 'futures';
            const active = mode === key;
            return (
              <button key={key} onClick={() => setMode(key)} style={{
                padding: '12px 20px', background: 'none', border: 'none',
                color: active ? C.text : C.textTertiary, fontSize: 15, fontWeight: active ? 600 : 400, position: 'relative',
              }}>
                {label}
                {active && <div style={{ position: 'absolute', bottom: 0, left: '50%', transform: 'translateX(-50%)', width: '60%', height: 2, background: C.text, borderRadius: 1 }} />}
              </button>
            );
          })}
        </div>

        {/* Symbol Row */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', borderBottom: `1px solid ${C.border}` }}>
          <button onClick={() => setShowSymbols(true)} style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'none', border: 'none', padding: 0 }}>
            <CoinIcon symbol={symbol} size={28} />
            <span style={{ color: C.text, fontWeight: 700, fontSize: 16 }}>{symbol}</span>
            <ChevronDown size={18} color={C.textSecondary} />
            <span style={{ padding: '4px 8px', borderRadius: 4, fontSize: 12, fontWeight: 600, background: isUp ? C.longBg : C.shortBg, color: isUp ? C.long : C.short }}>{isUp ? '+' : ''}{change.toFixed(2)}%</span>
          </button>
          <button style={{ width: 36, height: 36, borderRadius: 8, background: C.bgTertiary, border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Star size={18} color={C.textSecondary} />
          </button>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: `1px solid ${C.border}`, paddingLeft: 8, overflowX: 'auto' }}>
          {[
            { id: 'chart', label: 'График' },
            { id: 'trade', label: 'Торговля' },
            { id: 'orders', label: 'Ордера' },
            { id: 'positions', label: mode === 'spot' ? 'Активы' : 'Позиции' },
            { id: 'history', label: 'История' },
          ].map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} className="tab-transition btn-press" style={{
              padding: '14px 12px', background: 'none', border: 'none',
              color: tab === t.id ? C.text : C.textTertiary, fontSize: 13, fontWeight: tab === t.id ? 600 : 400,
              borderBottom: tab === t.id ? `2px solid ${C.text}` : '2px solid transparent',
              whiteSpace: 'nowrap',
            }}>{t.label}</button>
          ))}
        </div>
      </div>

      {/* Live Price Row - Compact for Chart, Full for Trade */}
      {tab === 'chart' && (
        <div className="animate-fadeIn" style={{ 
          padding: '8px 16px', 
          background: C.bgSecondary, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          borderBottom: `1px solid ${C.border}`,
        }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
            <span className="animate-pulse" style={{ 
              color: isUp ? C.long : C.short, 
              fontSize: 20, 
              fontWeight: 700, 
              fontFamily: 'monospace',
              letterSpacing: '-0.5px',
            }}>
              {price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 7 })}
            </span>
            <span style={{ 
              padding: '2px 6px', 
              borderRadius: 4, 
              fontSize: 11, 
              fontWeight: 600, 
              background: isUp ? C.longBg : C.shortBg, 
              color: isUp ? C.long : C.short,
            }}>
              {isUp ? '▲' : '▼'} {Math.abs(change).toFixed(2)}%
            </span>
          </div>
          <div style={{ display: 'flex', gap: 12, fontSize: 10 }}>
            <span><span style={{ color: C.textTertiary }}>H </span><span style={{ color: C.long }}>{high24.toLocaleString()}</span></span>
            <span><span style={{ color: C.textTertiary }}>L </span><span style={{ color: C.short }}>{low24.toLocaleString()}</span></span>
          </div>
        </div>
      )}
      
      {tab === 'trade' && (
        <div style={{ padding: '10px 16px', background: C.bgSecondary }}>
          <span style={{ color: isUp ? C.long : C.short, fontSize: 26, fontWeight: 700, fontFamily: 'monospace' }}>
            {price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 7 })}
          </span>
          <div style={{ display: 'flex', gap: 16, marginTop: 6 }}>
            <span style={{ fontSize: 11 }}><span style={{ color: C.textTertiary }}>High </span><span style={{ color: C.long }}>{high24.toLocaleString()}</span></span>
            <span style={{ fontSize: 11 }}><span style={{ color: C.textTertiary }}>Low </span><span style={{ color: C.short }}>{low24.toLocaleString()}</span></span>
            <span style={{ fontSize: 11 }}><span style={{ color: C.textTertiary }}>Vol </span><span style={{ color: C.text }}>{formatVol(vol24)}</span></span>
          </div>
        </div>
      )}

      {/* Content */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden', position: 'relative' }}>
        {tab === 'chart' && (
          <div style={{ 
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1,
          }}>
            <TradingChart symbol={symbol} />
          </div>
        )}

        {tab === 'trade' && (
          <div style={{ flex: 1, display: 'flex', padding: '8px', gap: 6, overflow: 'hidden' }}>
            {/* Trade Form */}
            <div style={{ flex: '1 1 50%', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 8, overflow: 'auto', position: 'relative', zIndex: 2 }}>
              
              {/* Buy/Sell Toggle - TOP */}
              <div style={{ 
                display: 'flex', 
                background: C.bgTertiary, 
                borderRadius: 8, 
                padding: 3,
                marginBottom: 4,
              }}>
                <button 
                  onClick={() => setTradeSide('long')} 
                  className="btn-press"
                  style={{ 
                    flex: 1, 
                    padding: '10px 8px', 
                    borderRadius: 6, 
                    border: 'none',
                    background: tradeSide === 'long' ? C.long : 'transparent',
                    color: tradeSide === 'long' ? '#fff' : C.textSecondary,
                    fontSize: 13, 
                    fontWeight: 600,
                    transition: 'all 0.2s ease',
                  }}
                >
                  {mode === 'futures' ? 'Long' : 'Купить'}
                </button>
                <button 
                  onClick={() => setTradeSide('short')} 
                  className="btn-press"
                  style={{ 
                    flex: 1, 
                    padding: '10px 8px', 
                    borderRadius: 6, 
                    border: 'none',
                    background: tradeSide === 'short' ? C.short : 'transparent',
                    color: tradeSide === 'short' ? '#fff' : C.textSecondary,
                    fontSize: 13, 
                    fontWeight: 600,
                    transition: 'all 0.2s ease',
                  }}
                >
                  {mode === 'futures' ? 'Short' : 'Продать'}
                </button>
              </div>

              {mode === 'futures' && (
                <button onClick={() => setShowLeverageModal(true)} style={{
                  padding: '10px 12px', borderRadius: 8, background: C.yellowSoft, border: 'none',
                  color: C.yellow, fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                }}>Плечо: {leverage}x <ChevronDown size={14} /></button>
              )}

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 4px' }}>
                <span style={{ color: C.textTertiary, fontSize: 11 }}>Доступно</span>
                <span style={{ color: C.text, fontSize: 12, fontWeight: 500 }}>{balance.toFixed(2)} USDT</span>
              </div>

              {mode === 'spot' && spotAssetBalance > 0 && (
                <div 
                  onClick={() => {
                    const assetValueInUsdt = spotAssetBalance * price;
                    setAmount(assetValueInUsdt.toFixed(2));
                    setSliderPct(100);
                  }}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 10px', background: C.bgTertiary, borderRadius: 6, cursor: 'pointer' }}>
                  <span style={{ color: C.textTertiary, fontSize: 10 }}>Баланс {base}</span>
                  <span style={{ color: C.yellow, fontSize: 11, fontWeight: 600 }}>{spotAssetBalance.toFixed(6)}</span>
                </div>
              )}

              <button onClick={() => setShowOrderTypeModal(true)} style={{
                padding: '10px 12px', borderRadius: 6, background: C.bgElevated, border: 'none',
                color: C.text, fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}>{orderType === 'market' ? 'Рыночный' : 'Лимитный'} <ChevronDown size={12} color={C.textTertiary} /></button>

              {orderType === 'limit' && (
                <div 
                  onClick={(e) => { e.currentTarget.querySelector('input')?.focus(); }}
                  style={{ display: 'flex', alignItems: 'center', background: C.bgInput, borderRadius: 6, padding: '8px 10px', border: `1px solid ${C.border}`, cursor: 'text', position: 'relative', zIndex: 2 }}>
                  <span style={{ color: C.textTertiary, fontSize: 10, pointerEvents: 'none', flexShrink: 0 }}>Цена</span>
                  <input type="number" value={limitPrice} onChange={(e) => setLimitPrice(e.target.value)} placeholder={price.toFixed(2)}
                    style={{ flex: 1, background: 'none', border: 'none', outline: 'none', color: C.text, fontSize: 16, fontFamily: 'monospace', textAlign: 'right', padding: '4px', minWidth: 0 }} />
                  <span style={{ color: C.textTertiary, fontSize: 10, pointerEvents: 'none', flexShrink: 0 }}>USDT</span>
                </div>
              )}

              <div 
                onClick={(e) => { e.currentTarget.querySelector('input')?.focus(); }}
                style={{ display: 'flex', alignItems: 'center', background: C.bgInput, borderRadius: 6, padding: '8px 10px', border: `1px solid ${C.border}`, position: 'relative', zIndex: 2, cursor: 'text' }}>
                <span style={{ color: C.textTertiary, fontSize: 10, flexShrink: 0, pointerEvents: 'none' }}>Сумма</span>
                <input type="number" value={amount} onChange={(e) => { setAmount(e.target.value); setSliderPct(0); }} placeholder="0.00"
                  style={{ flex: 1, background: 'none', border: 'none', outline: 'none', color: C.text, fontSize: 16, fontFamily: 'monospace', textAlign: 'right', padding: '4px', minWidth: 0 }} />
                <span style={{ color: C.textTertiary, fontSize: 10, flexShrink: 0, pointerEvents: 'none' }}>USDT</span>
              </div>

              {/* Slider */}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 0, padding: '2px 0' }}>
                  {[0, 25, 50, 75, 100].map((pct) => (
                    <React.Fragment key={pct}>
                      <button onClick={() => handleSlider(pct)} style={{ width: 10, height: 10, borderRadius: '50%', background: sliderPct >= pct ? C.yellow : C.textMuted, border: 'none' }} />
                      {pct < 100 && <div style={{ flex: 1, height: 2, background: sliderPct > pct ? C.yellow : C.textMuted }} />}
                    </React.Fragment>
                  ))}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 2 }}>
                  {[0, 25, 50, 75, 100].map(pct => (
                    <span key={pct} onClick={() => handleSlider(pct)} style={{ color: sliderPct === pct ? C.yellow : C.textTertiary, fontSize: 9, cursor: 'pointer', fontWeight: sliderPct === pct ? 600 : 400 }}>{pct}%</span>
                  ))}
                </div>
              </div>

              {mode === 'futures' && amount && parseFloat(amount) > 0 && (
                <div style={{ padding: 8, borderRadius: 6, background: C.bgTertiary, fontSize: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ color: C.textTertiary }}>Размер ({leverage}x)</span>
                    <span style={{ color: C.text, fontFamily: 'monospace' }}>{((parseFloat(amount) * leverage) / price).toFixed(4)} {base}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: C.textTertiary }}>Цена ликв.</span>
                    <span style={{ color: C.short, fontFamily: 'monospace' }}>${(price * (1 - 0.9 / leverage)).toFixed(2)}</span>
                  </div>
                </div>
              )}

              {/* Spot: Show coin quantity being purchased/sold */}
              {mode === 'spot' && amount && parseFloat(amount) > 0 && (
                <div style={{ 
                  padding: '10px 12px', 
                  borderRadius: 8, 
                  background: tradeSide === 'long' ? C.longBg : C.shortBg, 
                  border: `1px solid ${tradeSide === 'long' ? 'rgba(34, 197, 94, 0.2)' : 'rgba(239, 68, 68, 0.2)'}`,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ color: C.textSecondary, fontSize: 11 }}>
                      {tradeSide === 'long' ? 'Вы получите' : 'Вы продадите'}
                    </span>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                      <span style={{ 
                        color: tradeSide === 'long' ? C.long : C.short, 
                        fontSize: 16, 
                        fontWeight: 700, 
                        fontFamily: 'monospace' 
                      }}>
                        {(parseFloat(amount) / price).toFixed(6)}
                      </span>
                      <span style={{ 
                        color: tradeSide === 'long' ? C.long : C.short, 
                        fontSize: 12, 
                        fontWeight: 600 
                      }}>
                        {base}
                      </span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 6 }}>
                    <span style={{ color: C.textTertiary, fontSize: 10 }}>
                      Цена: ${price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 7 })}
                    </span>
                    <span style={{ color: C.textTertiary, fontSize: 10 }}>
                      ≈ ${parseFloat(amount).toFixed(2)} USDT
                    </span>
                  </div>
                </div>
              )}

              {msg.text && (
                <div className="animate-scaleIn" style={{ padding: '8px 10px', borderRadius: 6, background: msg.type === 'error' ? C.shortBg : C.longBg, color: msg.type === 'error' ? C.short : C.long, fontSize: 11 }}>{msg.text}</div>
              )}

              <div style={{ flex: 1, minHeight: 8 }} />

              {/* Single Action Button - changes based on tradeSide */}
              <button 
                onClick={() => handleTrade(tradeSide)} 
                disabled={loading || (mode === 'spot' && tradeSide === 'short' && spotAssetBalance <= 0)} 
                className="btn-press" 
                style={{
                  width: '100%', 
                  padding: 14, 
                  borderRadius: 8, 
                  background: tradeSide === 'long' ? C.long : C.short, 
                  border: 'none',
                  color: '#fff', 
                  fontSize: 15, 
                  fontWeight: 700, 
                  opacity: loading || (mode === 'spot' && tradeSide === 'short' && spotAssetBalance <= 0) ? 0.5 : 1,
                  transition: 'all 0.2s ease',
                }}
              >
                {loading ? 'Загрузка...' : (
                  tradeSide === 'long' 
                    ? (mode === 'futures' ? `Long ${base}` : `Купить ${base}`)
                    : (mode === 'futures' ? `Short ${base}` : `Продать ${base}`)
                )}
              </button>
            </div>

            {/* Order Book */}
            <div style={{ flex: '0 0 50%', minWidth: 150, overflow: 'hidden' }}>
              <OrderBook symbol={symbol} compact />
            </div>
          </div>
        )}

        {tab === 'positions' && (
          <div style={{ flex: 1, overflow: 'auto' }}>
            {mode === 'futures' ? (
              <Positions refresh={refresh} onClose={() => setRefresh(r => r + 1)} />
            ) : (
              <SpotHoldings holdings={spotHoldings} onSell={(h) => { setSymbol(h.symbol + 'USDT'); setTab('trade'); }} />
            )}
          </div>
        )}

        {tab === 'orders' && (
          <div style={{ flex: 1, overflow: 'auto' }}>
            <PendingOrders refresh={refresh} mode={mode} />
          </div>
        )}

        {tab === 'history' && (
          <div style={{ flex: 1, overflow: 'auto' }}>
            <TradeHistory mode={mode} />
          </div>
        )}
      </div>

      {/* Buy/Sell Buttons - Fixed above Bottom Nav (Chart tab only) - MINI */}
      {tab === 'chart' && (
        <div style={{ 
          position: 'fixed', 
          bottom: 80, 
          left: 0, 
          right: 0, 
          display: 'flex', 
          gap: 8, 
          padding: '4px 16px',
          background: 'transparent',
          zIndex: 99,
        }}>
          <button 
            onClick={() => { setTab('trade'); }} 
            className="btn-press"
            style={{ 
              flex: 1, 
              padding: '7px 12px', 
              background: C.long, 
              border: 'none', 
              color: '#fff', 
              fontSize: 12, 
              fontWeight: 600,
              borderRadius: 6,
              transition: 'transform 0.1s ease',
            }}
          >
            Купить
          </button>
          <button 
            onClick={() => { setTab('trade'); }} 
            className="btn-press"
            style={{ 
              flex: 1, 
              padding: '7px 12px', 
              background: C.short, 
              border: 'none', 
              color: '#fff', 
              fontSize: 12, 
              fontWeight: 600,
              borderRadius: 6,
              transition: 'transform 0.1s ease',
            }}
          >
            Продать
          </button>
        </div>
      )}

      {/* Bottom Nav - BYBIT STYLE with center highlight */}
      <nav style={{ 
        position: 'fixed', 
        bottom: 0, 
        left: 0, 
        right: 0, 
        display: 'flex', 
        justifyContent: 'space-around',
        alignItems: 'center',
        paddingBottom: 'max(8px, env(safe-area-inset-bottom))', 
        background: C.bgSecondary, 
        borderTop: `1px solid ${C.border}`,
        zIndex: 100,
        height: 72,
      }}>
        {[
          { icon: Home, label: 'Главная', path: '/' },
          { icon: Rocket, label: 'IDO', path: '/ido' },
          { icon: TrendingUp, label: 'Трейдинг', path: '/trading', primary: true },
          { icon: Wallet, label: 'Кошелёк', path: '/wallet' },
          { icon: User, label: 'Профиль', path: '/profile' },
        ].map((item, i) => {
          if (item.primary) {
            return (
              <button key={i} style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                background: 'none', border: 'none', marginTop: -20,
              }}>
                <div style={{
                  width: 56, height: 56, borderRadius: '50%',
                  background: C.yellow, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: '0 4px 12px rgba(240, 185, 11, 0.4)',
                }}>
                  <item.icon size={26} color="#000" strokeWidth={2.5} />
                </div>
                <span style={{ fontSize: 10, marginTop: 4, color: C.yellow, fontWeight: 600 }}>{item.label}</span>
              </button>
            );
          }
          return (
            <button key={i} onClick={() => navigate(item.path)} style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              background: 'none', border: 'none', padding: '8px 12px', minWidth: 60,
            }}>
              <item.icon size={22} color={C.textSecondary} strokeWidth={2} />
              <span style={{ fontSize: 10, marginTop: 4, color: C.textSecondary, fontWeight: 400 }}>{item.label}</span>
            </button>
          );
        })}
      </nav>

      {/* Modals */}
      <SymbolPicker open={showSymbols} onClose={() => setShowSymbols(false)} onSelect={handleSymbolChange} current={symbol} />

      {/* Leverage Modal with Slider */}
      {showLeverageModal && (
        <div className="animate-fadeIn" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000, display: 'flex', alignItems: 'flex-end' }} onClick={() => setShowLeverageModal(false)}>
          <div className="animate-slideUp" style={{ width: '100%', background: C.bgSecondary, borderRadius: '16px 16px 0 0', paddingBottom: 'env(safe-area-inset-bottom)' }} onClick={e => e.stopPropagation()}>
            <div style={{ width: 40, height: 4, background: C.textMuted, borderRadius: 2, margin: '12px auto' }} />
            <div style={{ padding: '8px 16px 24px' }}>
              <h3 style={{ color: C.text, fontSize: 18, fontWeight: 600, marginBottom: 20, textAlign: 'center' }}>Плечо: {leverage}x</h3>
              
              {/* Slider */}
              <div style={{ padding: '0 10px', marginBottom: 20 }}>
                <input
                  type="range"
                  min="1"
                  max="125"
                  value={leverage}
                  onChange={(e) => setLeverage(parseInt(e.target.value))}
                  style={{ width: '100%', height: 4, appearance: 'none', background: C.bgTertiary, borderRadius: 2, outline: 'none' }}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, color: C.textTertiary, fontSize: 11 }}>
                  <span>1x</span><span>25x</span><span>50x</span><span>75x</span><span>100x</span><span>125x</span>
                </div>
              </div>

              {/* Quick Select Buttons */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center' }}>
                {[1, 5, 10, 20, 50, 100].map(lev => (
                  <button key={lev} onClick={() => setLeverage(lev)} className="btn-press" style={{
                    width: 50, height: 40, borderRadius: 8,
                    background: leverage === lev ? C.yellow : C.bgTertiary,
                    color: leverage === lev ? '#000' : C.text,
                    border: 'none', fontSize: 13, fontWeight: 600,
                  }}>{lev}x</button>
                ))}
              </div>

              <button onClick={() => setShowLeverageModal(false)} style={{
                width: '100%', marginTop: 20, padding: 14, borderRadius: 8,
                background: C.yellow, border: 'none', color: '#000', fontSize: 15, fontWeight: 700,
              }}>Подтвердить</button>
            </div>
          </div>
        </div>
      )}

      {/* Order Type Modal */}
      {showOrderTypeModal && (
        <div className="animate-fadeIn" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000, display: 'flex', alignItems: 'flex-end' }} onClick={() => setShowOrderTypeModal(false)}>
          <div className="animate-slideUp" style={{ width: '100%', background: C.bgSecondary, borderRadius: '16px 16px 0 0', paddingBottom: 'env(safe-area-inset-bottom)' }} onClick={e => e.stopPropagation()}>
            <div style={{ width: 40, height: 4, background: C.textMuted, borderRadius: 2, margin: '12px auto' }} />
            <div style={{ padding: '8px 16px 20px' }}>
              <h3 style={{ color: C.text, fontSize: 18, fontWeight: 600, marginBottom: 16, textAlign: 'center' }}>Тип ордера</h3>
              {['market', 'limit'].map(type => (
                <button key={type} onClick={() => { setOrderType(type); setShowOrderTypeModal(false); }} className="btn-press" style={{
                  width: '100%', padding: '14px', marginBottom: 8, borderRadius: 10, border: 'none',
                  background: orderType === type ? C.yellowSoft : C.bgTertiary,
                  color: orderType === type ? C.yellow : C.text,
                  fontSize: 15, fontWeight: 500, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  transition: 'all 0.15s ease',
                }}><span>{type === 'market' ? 'Рыночный' : 'Лимитный'}</span>{orderType === type && <Check size={20} />}</button>
              ))}
            </div>
          </div>
        </div>
      )}

      <style>{`
        input::-webkit-outer-spin-button, input::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
        input[type=number] { -moz-appearance: textfield; }
        input[type=range]::-webkit-slider-thumb { -webkit-appearance: none; width: 20px; height: 20px; background: #f0b90b; border-radius: 50%; cursor: pointer; }
        input[type=range]::-moz-range-thumb { width: 20px; height: 20px; background: #f0b90b; border-radius: 50%; cursor: pointer; border: none; }
      `}</style>
    </div>
  );
};

export default PremiumMobileTradingPage;
